





















































import { Component, Prop, Vue } from 'vue-property-decorator';

import CloseIcon from '@/components/icons/Close.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import NavButtonItem from '@/componentsV2/molecules/NavButtonItem/NavButtonItem.vue';

import BillingService from '@/api/BillingService';
import HTTPStatus from '@/constants/httpStatus';

import eventBus from '@/utils/eventBus';

import CoinNetworkConcept from './Steps/CoinNetworkConcept.vue';
import CoinNetworkCharges from './Steps/CoinNetworkCharges.vue';
import CoinNetworkDetails from './Steps/CoinNetworkDetails.vue';
import CoinNetworkTerms from './Steps/CoinNetworkTerms.vue';
import CoinNetworkAddLocation from './Steps/CoinNetworkAddLocation.vue';
import { ValidationObserver } from 'vee-validate-3';
import { ModalOptionsV2 } from '@/components/Modal/ModalOptions';
import { EBillingStatusTypes } from '@/store/modules/businessTypes';
import flagKeys from '@/constants/flagKeys';
import segmentIo from '@/utils/segment.io';
import BillingModel from '@/views/Models/BillingModel.vue';
import { IBusinessType } from '@/models/business';
import { getNeedsOnboardingCall } from '@/utils/onboardingCall';
import { isRemoteLoading } from '@/utils/remote-loading';

export enum EBillingAcceptanceModalStepSlug {
  CONCEPT = 'concept',
  CHARGES = 'charges',
  DETAILS = 'details',
  TERMS = 'terms',
  ADD_LOCATION = 'add-location'
}

@Component({
  components: {
    CloseIcon,
    Typography,
    FilledButton,
    NavButtonItem,
    CoinNetworkConcept,
    CoinNetworkCharges,
    CoinNetworkTerms,
    CoinNetworkDetails,
    CoinNetworkAddLocation
  }
})
export default class CoinNetworkAcceptanceModal extends BillingModel {
  @Prop() welcomeCtaText!: string;
  @Prop() welcomeCtaClick?: () => void;
  @Prop() onSuccess?: () => void;
  @Prop() hideClose?: boolean;
  @Prop() origin?: string;
  @Prop() subscription?: any;

  observer: IntersectionObserver | null = null;
  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  subscriptionData: any = {};
  loading = false;
  disabled = false;

  acceptedTerms = false;

  service = new BillingService();

  activeStepSlug: string = EBillingAcceptanceModalStepSlug.CONCEPT;

  get tPrefix() {
    return 'modals.billing.acceptance';
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  get hasSteps() {
    return this.steps.length > 1;
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get billingModuleV3() {
    return this.flags.includes(flagKeys.billingModuleV3);
  }

  get delayedPeriod() {
    return this.billingModuleV3 && this.subscriptionData.eligibleForDelayPeriod;
  }

  get steps(): any[] {
    const steps: any = [
      {
        slug: EBillingAcceptanceModalStepSlug.CONCEPT,
        label: 'Billing Concept'
      },
      {
        slug: EBillingAcceptanceModalStepSlug.TERMS,
        label: 'Acceptance'
      }
    ];

    if (
      this.isBillingModuleV4 &&
      this.subscriptionData.locations &&
      !this.subscriptionData.locations.length
    ) {
      steps.splice(-1, 0, {
        slug: EBillingAcceptanceModalStepSlug.ADD_LOCATION,
        label: 'Add location',
        onClick: () => {
          eventBus.$emit(
            'createLocation',
            async () => {
              const step = this.steps[this.indexOfStep + 1];
              await this.loadSubscription();
              this.$store.dispatch('business/SET_BUSINESS_INFO', {
                ...this.businessInfo,
                type:
                  this.businessInfo.type === IBusinessType.ONLINE
                    ? IBusinessType.BOTH
                    : this.businessInfo.type
              });
              eventBus.$emit('reloadLocations', {
                showLoader: false,
                loading: false
              });

              this.triggerChangeStep(step);
            },
            false
          );
        }
      });
    }

    steps.splice(
      -1,
      0,
      this.delayedPeriod
        ? {
            slug: EBillingAcceptanceModalStepSlug.DETAILS,
            label: 'Details'
          }
        : {
            slug: EBillingAcceptanceModalStepSlug.CHARGES,
            label: 'Charges'
          }
    );

    return steps;
  }

  get indexOfStep() {
    return this.steps.findIndex(
      refStep => refStep.slug === this.activeStepSlug
    );
  }

  get tSecondaryButton() {
    let btn = 'back';

    if (this.activeStepSlug === this.steps[0].slug) {
      btn = this.hideClose ? 'learnMore' : 'cancel';
    }

    return `${this.tPrefix}.secondaryButton.${btn}`;
  }

  get tPrimaryButton() {
    let btn = 'continue';

    if (this.steps.length > 1 && this.activeStepSlug === this.steps[0].slug)
      btn = 'getStarted';

    if (this.activeStepSlug === this.steps[this.steps.length - 1].slug)
      btn = `confirm${this.billingModuleV3 ? 'Delayed' : ''}`;

    return `${this.tPrefix}.primaryButton.${btn}`;
  }

  get isRemoteLoading() {
    return isRemoteLoading();
  }

  get componentByStepSlug(): {
    [k: string]: {
      component: string;
    };
  } {
    return {
      [EBillingAcceptanceModalStepSlug.CONCEPT]: {
        component: 'CoinNetworkConcept'
      },
      [EBillingAcceptanceModalStepSlug.ADD_LOCATION]: {
        component: 'CoinNetworkAddLocation'
      },
      [EBillingAcceptanceModalStepSlug.CHARGES]: {
        component: 'CoinNetworkCharges'
      },
      [EBillingAcceptanceModalStepSlug.DETAILS]: {
        component: 'CoinNetworkDetails'
      },
      [EBillingAcceptanceModalStepSlug.TERMS]: {
        component: 'CoinNetworkTerms'
      }
    };
  }

  acceptTerms(value: boolean) {
    this.acceptedTerms = value;
  }

  changedLoading(value: boolean) {
    this.loading = value;
  }

  changedLocation(value: any) {
    this.disabled = !value.address || !value.locationName;
  }

  triggerChangeStep(step?: any) {
    const newStep = step || this.steps[this.indexOfStep + 1];

    segmentIo.track('Changed Step Billing Acceptance Flow', {
      'Actual Step': this.activeStepSlug,
      'Go To Step': newStep.slug,
      Origin: this.origin
    });
    this.activeStepSlug = newStep.slug;
  }

  onSecondaryClick() {
    this.disabled = false;

    if (this.activeStepSlug === this.steps[0].slug) {
      if (this.hideClose) {
        window.open(
          'https://support.twism.com/hc/en-us/articles/21224269134349-The-Coin-Network',
          '_blank'
        );
      } else {
        this.onClose();
      }
    } else {
      this.acceptedTerms = false;
      segmentIo.track('Changed Step Billing Acceptance Flow', {
        'Actual Step': this.activeStepSlug,
        'Go To Step': this.steps[this.indexOfStep - 1].slug,
        Origin: this.origin
      });
      this.activeStepSlug = this.steps[this.indexOfStep - 1].slug;
    }
  }

  onPrimaryClick() {
    this.disabled = false;
    if (this.indexOfStep === this.steps.length - 1) {
      segmentIo.track('Clicked Billing Acceptance Flow Finish', {
        Origin: this.origin
      });
      this.finish();
    } else {
      if (this.steps[this.indexOfStep].onClick) {
        this.steps[this.indexOfStep].onClick();
        return;
      }
      this.triggerChangeStep();
    }
  }

  async finish() {
    this.loading = true;
    try {
      let status = this.delayedPeriod
        ? EBillingStatusTypes.DELAY_PERIOD
        : EBillingStatusTypes.SUBSCRIBED;

      if (getNeedsOnboardingCall()) {
        status = EBillingStatusTypes.PENDING;
        this.$store.dispatch('business/SET_BILLING_SUBSCRIPTION', {
          ...this.subscription,
          status: EBillingStatusTypes.PENDING
        });
      }

      await this.service.acceptTermsSubscription();
      await this.service.activateSubscription();

      this.$store.dispatch('business/SET_BUSINESS_INFO', {
        ...this.businessInfo,
        type:
          this.businessInfo.type === IBusinessType.ONLINE
            ? IBusinessType.BOTH
            : this.businessInfo.type,
        subscriptionStatus: status
      });

      if (this.onSuccess) this.onSuccess();
      eventBus.$emit(
        'openSubModal',
        new ModalOptionsV2({
          componentToRender: 'coinNetworkWelcomeModal',
          hideCancelButton: false,
          icon: '',
          mobileFullScreen: true,
          fullWidth: !!this.isRemoteLoading
        }),
        {
          subscription: this.subscriptionData,
          ctaText: this.welcomeCtaText,
          ctaClick: this.welcomeCtaClick
        }
      );
    } finally {
      this.loading = false;
    }
  }

  isActiveStep(stepSlug: any) {
    return this.activeStepSlug === stepSlug;
  }

  onClose() {
    eventBus.$emit('closeModal');
  }

  percentageConcluded(step: any) {
    const indexOfStep = this.steps.findIndex(
      refStep => refStep.slug === step.slug
    );

    const currentIndex = this.steps.findIndex(
      refStep => refStep.slug === this.activeStepSlug.split(':')[0]
    );

    const isPrevLink = indexOfStep < currentIndex;

    return this.isActiveStep(step.slug) || isPrevLink ? 100 : 0;
  }

  async loadSubscription() {
    const {
      data,
      status
    } = await this.service.getBillingOverviewSubscription();

    this.$store.dispatch('business/SET_BILLING_SUBSCRIPTION', data);

    if (status === HTTPStatus.OK) {
      this.subscriptionData = data;
    }
  }

  async created() {
    segmentIo.modal('Billing Acceptance', {
      Origin: this.origin
    });
    if (this.subscription) {
      this.subscriptionData = this.subscription;
      return;
    }
    this.loadSubscription();
  }
}
